import React, { useState } from 'react'
import sadEmoji from 'assets/illustrations/sad_emoji.png'
import smileEmoji from 'assets/illustrations/smile_emoji.png'
import trophyEmoji from 'assets/illustrations/trophy_emoji.png'
import trophyGraphic from 'assets/illustrations/trophy_graphic.png'
import { ModalBase, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { Rating } from 'transfix-ui/components/Rating'
import { Select, ISelectProps } from 'transfix-ui/components/Select'
import { Tag } from 'transfix-ui/components/Tag'
import { Box, Typography, Link, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { ICarrierApplication } from 'types/FMSGraphqlTypes'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { InfoIcon, CloseIcon } from 'transfix-icons'
import { TAG_STYLE, TAG_SIZE } from 'transfix-ui'
import { TSelectOpt } from '../../../../transfix-ui/components/Select/SelectTypes'

interface ICarrierScorecardMyRatingProps {
  carrierApplication: Pick<ICarrierApplication, 'carrierScorecard' | 'id' | 'name'> | undefined
  tier: string
  showCarriersSelect: boolean
  carriers: TSelectOpt[]
  onSelectCarrierHandler: ISelectProps['onChange']
}

interface ICarrierScorecardTier {
  tagLabel: string
  tagStyle: TAG_STYLE
  showLabel: boolean
  showTrophy: boolean
  showTag: boolean
  copy: string
  modalTitle: string
  modalDescription: string
}

interface ICarrierScorecardTiers {
  [variant: string]: ICarrierScorecardTier
}

const useCarrierScorecardMyRatingStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(4),
  },
  main: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalItems: {
    display: 'flex',
  },
  titleText: {
    fontWeight: 'normal',
  },
  modalItem: {
    marginLeft: '32px',
  },
  alignment: {
    marginBottom: theme.spacing(1.5),
  },
  iconColor: {
    color: brandColors.skyBlue6,
  },
  trophyDiv: {
    maxWidth: '200px',
  },
  trophyGraphic: {
    height: '90%',
    width: '100%',
  },
  emojiGraphics: {
    height: '50%',
    width: '100%',
  },
  modalLink: {
    marginLeft: theme.spacing(1),
  },
  scoreBox: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: '14px',
  },
  scoreElements: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '14px',
  },
  closeButton: {
    width: '30px',
    height: '30px',
    padding: theme.spacing(0.5),
    color: brandColors.coolGray5,
    backgroundColor: brandColors.coolGray2,
    borderRadius: '50px',
    '&:hover': {
      color: brandColors.skyBlue7,
      backgroundColor: brandColors.skyBlue2,
    },
  },
}))

const tiers: ICarrierScorecardTiers = {
  top_tier: {
    tagLabel: 'Top tier',
    tagStyle: TAG_STYLE.success,
    showLabel: true,
    showTrophy: true,
    showTag: true,
    copy: 'You have been delivering top-tier service. Keep up the great work.',
    modalTitle: '5 stars (Top Tier)',
    modalDescription:
      'You’re our rock star carrier! Carriers that deliver exceptional quality at every turn will receive Transfix’s best loads and newest opportunities.',
  },
  keep_going: {
    tagLabel: '',
    tagStyle: TAG_STYLE.success,
    showLabel: false,
    showTrophy: true,
    showTag: false,
    copy:
      'To bump up into the top tier of Transfix carriers, make sure you’re meeting the criteria of each rating category for every new load.',
    modalTitle: '3-4.99 stars (Keep going)',
    modalDescription:
      'We consider you a trusted carrier. We appreciate your service to Transfix and we look forward to you continuing to run loads using Transfix.',
  },
  needs_improvement: {
    tagLabel: 'Needs improvement',
    tagStyle: TAG_STYLE.warning,
    showLabel: false,
    showTrophy: false,
    showTag: true,
    copy:
      'Every load is a chance to improve your rating. Ensure you’re meeting the criteria for each rating category to ensure that you’ll continue to be eligible to receive load offers.',
    modalTitle: 'Less than 3 stars (Needs Improvement)',
    modalDescription:
      'We’ll need to see a bit better quality out of the loads you run and may restrict access to some functions or stop sending new loads to you.',
  },
}

const CarrierScorecardMyRating = ({
  carriers = [],
  carrierApplication,
  onSelectCarrierHandler,
  showCarriersSelect,
  tier,
}: ICarrierScorecardMyRatingProps) => {
  const [isOpen, setOpen] = useState<boolean>(false)
  const close = () => setOpen(false)
  const open = () => setOpen(true)
  const classes = useCarrierScorecardMyRatingStyles()
  const { isMobile, isTablet } = useBreakpointContext()
  const currentRating = carrierApplication?.carrierScorecard?.overallScore || 0

  const { stale: isStale } = carrierApplication?.carrierScorecard || {}

  return (
    <div className={classes.container} data-test='carrier-rating-scorecard-page'>
      <div className={classes.main}>
        <div>
          <Box className={classes.alignment} mb={3}>
            <Typography variant='h2' className={classes.titleText}>
              My Rating
            </Typography>
          </Box>
          {showCarriersSelect && (
            <Box maxWidth={230} className={classes.alignment}>
              <Select
                options={carriers}
                value={carrierApplication?.id || carriers[0]?.value}
                placeholder='Carrier'
                onChange={onSelectCarrierHandler}
              />
            </Box>
          )}
          <Box className={classes.scoreBox}>
            <div>
              <Typography variant='subtitle1' display='inline'>
                Current rating
              </Typography>
            </div>
            <div className={classes.scoreElements}>
              <Rating
                size='large'
                value={isStale ? 0 : currentRating}
                defaultValue={0}
                precision={0.5}
              />
              <Typography variant='h3' display='inline' data-test='carrier-rating-score'>
                {isStale ? '--' : currentRating}
              </Typography>
              {!isStale && tiers[tier]?.showTag && (
                <Tag
                  label={tiers[tier]?.tagLabel}
                  size={isMobile ? TAG_SIZE.xsmall : TAG_SIZE.small}
                  tagStyle={tiers[tier]?.tagStyle}
                />
              )}
            </div>
          </Box>
          <Box className={classes.alignment} mt={2}>
            <Typography variant='body1'>
              {isStale
                ? 'Your carrier rating is not visible until after you run your first load.'
                : tiers[tier]?.copy}
            </Typography>
          </Box>
          <Box display='flex'>
            <InfoIcon color='skyBlue6' size='large' />
            <Link onClick={open} className={classes.modalLink}>
              What does my rating mean?
            </Link>
            <ModalBase
              modalType={MODAL_TYPES.confirmation}
              onClose={close}
              open={isOpen}
              closeOnBackdropClick
              size={isTablet ? 7 : 5}>
              <Box display='flex' alignItems='center' className={classes.main}>
                <Typography variant='h3'>What does my rating mean?</Typography>
                <div onClick={close} aria-hidden='true'>
                  <CloseIcon size='xxlarge' className={classes.closeButton} />
                </div>
              </Box>
              <Box mt={2} mb={2}>
                <Typography variant='body2'>
                  Your rating is a way to recognize and reward Transfix carriers who consistently
                  provide exceptional service and to inform carriers how to improve their
                  performance.
                </Typography>
              </Box>

              <Box display='flex' alignItems='center' mt={4}>
                <div>
                  <img src={trophyEmoji} className={classes.emojiGraphics} alt='trophy_emoji' />
                </div>
                <div className={classes.modalItem}>
                  <Typography variant='h5'>{tiers.top_tier?.modalTitle}</Typography>
                  <Typography variant='body2'>{tiers.top_tier?.modalDescription}</Typography>
                </div>
              </Box>
              <Box display='flex' alignItems='center' mt={2}>
                <div>
                  <img src={smileEmoji} className={classes.emojiGraphics} alt='smile_emoji' />
                </div>
                <div className={classes.modalItem}>
                  <Typography variant='h5'>{tiers.keep_going?.modalTitle}</Typography>
                  <Typography variant='body2'>{tiers.keep_going?.modalDescription}</Typography>
                </div>
              </Box>
              <Box display='flex' alignItems='center' mt={2} mb={3}>
                <div>
                  <img src={sadEmoji} className={classes.emojiGraphics} alt='sad_emoji' />
                </div>
                <div className={classes.modalItem}>
                  <Typography variant='h5'>{tiers.needs_improvement?.modalTitle}</Typography>
                  <Typography variant='body2'>
                    {tiers.needs_improvement?.modalDescription}
                  </Typography>
                </div>
              </Box>
            </ModalBase>
          </Box>
        </div>
        {!isMobile && tiers[tier]?.showTrophy && (
          <div className={classes.trophyDiv}>
            <img src={trophyGraphic} className={classes.trophyGraphic} alt='trophy_graphic' />
          </div>
        )}
      </div>
    </div>
  )
}

export default CarrierScorecardMyRating
